"use client";

import React from "react";
import { motion } from "framer-motion";
import { Loader as Loader2, MapPin, Phone, Mail, Clock, User, CircleCheck as CheckCircle2, CircleAlert as AlertCircle } from 'lucide-react';
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
function ContactFormInner() {
  const honeypotRef = React.useRef<HTMLInputElement>(null);
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} className="flex flex-col items-center justify-center space-y-4 py-12 text-center">
        <div className="rounded-lg bg-primary/10 p-3 text-primary">
          <CheckCircle2 className="size-12" />
        </div>
        <h3 className="font-default text-2xl font-semibold text-foreground">Request Received</h3>
        <p className="text-muted-foreground max-w-md">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6">
          Submit Another Request
        </Button>
      </motion.div>;
  }
  return <Form onSubmit={e => {
    if (honeypotRef.current && honeypotRef.current.value) {
      e.preventDefault();
      return;
    }
    return handleSubmit(e);
  }}>
      <input type="text" name="website" ref={honeypotRef} tabIndex={-1} autoComplete="off" style={{
      position: "absolute",
      left: "-9999px",
      width: "1px",
      height: "1px",
      opacity: 0
    }} aria-hidden="true" />
      <div className="grid gap-6 md:grid-cols-2">
        <FormField control={control} name="name" rules={{
        required: "Full name is required",
        minLength: {
          value: 2,
          message: "Name must be at least 2 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Full Name</FormLabel>
              <FormControl>
                <div className="relative">
                  <User className="absolute left-3 top-2.5 size-4 text-muted-foreground" />
                  <Input className="pl-9" {...field} />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email address is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Email Address</FormLabel>
              <FormControl>
                <div className="relative">
                  <Mail className="absolute left-3 top-2.5 size-4 text-muted-foreground" />
                  <Input type="email" className="pl-9" {...field} />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required",
        minLength: {
          value: 10,
          message: "Please enter a valid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Phone Number</FormLabel>
              <FormControl>
                <div className="relative">
                  <Phone className="absolute left-3 top-2.5 size-4 text-muted-foreground" />
                  <Input type="tel" className="pl-9" {...field} />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="location" rules={{
        required: "Please select a preferred location"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Preferred Location</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="w-full pl-3">
                    <div className="flex items-center gap-2">
                      <MapPin className="size-4 text-muted-foreground" />
                      <SelectValue placeholder="Select an office" />
                    </div>
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="delray">Delray Beach Office</SelectItem>
                  <SelectItem value="boynton">Boynton Beach Office</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="message" rules={{
        required: "Please provide a brief message or reason for visit",
        minLength: {
          value: 10,
          message: "Message must be at least 10 characters"
        }
      }} render={({
        field
      }) => <FormItem className="md:col-span-2">
              <FormLabel>How can we help you?</FormLabel>
              <FormControl>
                <Textarea placeholder="Please describe your symptoms or reason for requesting an appointment..." className="min-h-[120px] resize-y" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {submitError && <div className="mt-6 flex items-center gap-2 rounded-md bg-destructive/10 p-4 text-destructive">
          <AlertCircle className="size-5 shrink-0" />
          <p className="text-sm">{submitError}</p>
        </div>}

      <div className="mt-8">
        <Button type="submit" variant="destructive" size="lg" disabled={isSubmitting} className="w-full sm:w-auto">
          {isSubmitting ? <>
              <Loader2 className="mr-2 size-5 animate-spin" />
              Sending Request...
            </> : "Request Appointment"}
        </Button>
        <p className="mt-4 text-center text-xs text-muted-foreground sm:text-left">
          * In case of a medical emergency, please call 911 immediately. This form is for non-urgent appointment requests only.
        </p>
      </div>
    </Form>;
}
export default function ClinicalContactForm() {
  const phone = "(561) 496-7900";
  const email = "frontdesk@floridapremiercardio.com";
  return <>
    <section id="clinicalcontactform" className="bg-background pb-16 pt-6 md:pb-24 md:pt-8 lg:pb-32 lg:pt-10">
      <div className="container mx-auto px-4 md:px-6">
        <div className="mx-auto max-w-3xl">
          <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            amount: 0.2
          }} transition={{
            duration: 0.5
          }} className="mb-10 text-center md:mb-12">
            <h2 className="font-serif text-4xl md:text-5xl font-medium tracking-[-0.02em] leading-[1.1] text-foreground">
              Request an Appointment
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg">
              Request an appointment at our Delray Beach or Boynton Beach office. Our team will contact you shortly to confirm your visit.
            </p>
          </motion.div>
        </div>

        <div className="mx-auto grid max-w-6xl grid-cols-1 items-start gap-8 lg:grid-cols-12 lg:gap-12">

          <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            amount: 0.2
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="lg:col-span-7">
            <Card className="border-border/50 shadow-lg">
              <CardHeader className="bg-muted/30 pb-8">
                <CardTitle className="font-default text-xl">Patient Information</CardTitle>
                <CardDescription>
                  Please fill out the form below and our scheduling team will reach out to you.
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-8">
                <FormProvider formId="clinical-appointment-request" sectionName="ClinicalContactForm" defaultValues={{
                  name: "",
                  email: "",
                  phone: "",
                  location: "",
                  message: ""
                }} submitText="Request Appointment" successMessage="Thank you for requesting an appointment. Our office will contact you within 1 business day to confirm your schedule.">
                  <ContactFormInner />
                </FormProvider>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            amount: 0.2
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="lg:col-span-5">
            <Card className="bg-secondary text-secondary-foreground border-none rounded-2xl p-8 relative overflow-hidden shadow-2xl">
              <div className="absolute inset-0 bg-primary opacity-5 pointer-events-none" />
              <div className="relative z-10 space-y-8">
                <div>
                  <h3 className="font-mono text-xs font-medium tracking-[0.2em] uppercase text-primary mb-3">
                    Phone Number
                  </h3>
                  <div>
                    <a href={`tel:${phone.replace(/\s+/g, "")}`} className="inline-flex items-start gap-3 hover:text-primary transition-colors">
                      <Phone className="mt-0.5 size-5 shrink-0 text-primary" />
                      <span className="flex flex-col">
                        <span className="text-lg font-medium leading-none sm:text-xl">{phone}</span>
                        <span className="mt-2 text-sm text-secondary-foreground/70">Delray &amp; Boynton Offices</span>
                      </span>
                    </a>
                  </div>
                </div>
                <Separator className="bg-secondary-foreground/10" />
                <div>
                  <h3 className="font-mono text-xs font-medium tracking-[0.2em] uppercase text-primary mb-3">
                    Email Address
                  </h3>
                  <a href={`mailto:${email}`} className="flex items-center gap-3 text-lg hover:text-primary transition-colors break-all">
                    <Mail className="w-5 h-5 text-primary shrink-0" />
                    <span>{email}</span>
                  </a>
                </div>
                <Separator className="bg-secondary-foreground/10" />
                <div>
                  <h3 className="font-mono text-xs font-medium tracking-[0.2em] uppercase text-primary mb-3">
                    Office Hours
                  </h3>
                  <div className="flex items-start gap-3 text-base">
                    <Clock className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                    <span className="flex flex-col">
                      <span className="font-medium">Monday to Friday</span>
                      <span className="mt-1 text-secondary-foreground/70">8:30 AM–5:30 PM</span>
                    </span>
                  </div>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>

      <section id="clinicalcontactmap" className="bg-background pb-16 md:pb-24 lg:pb-32">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <h3 className="font-mono text-xs font-medium tracking-[0.2em] uppercase text-primary mb-3">
              Our Locations
            </h3>
            <h2 className="font-serif text-3xl md:text-4xl font-medium text-foreground">
              Delray Beach &amp; Boynton Beach
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex flex-col gap-3">
              <div className="relative h-[350px] w-full overflow-hidden rounded-2xl border border-border">
                <iframe src="https://www.google.com/maps?q=5210+Linton+Blvd+Suite+301+Delray+Beach+FL+33484&output=embed" className="h-full w-full" style={{
                border: 0
              }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Map of Delray Beach location"></iframe>
              </div>
              <p className="text-center font-sans text-sm text-muted-foreground">
                Delray Beach — 5210 Linton Blvd Suite 301
              </p>
            </div>
            <div className="flex flex-col gap-3">
              <div className="relative h-[350px] w-full overflow-hidden rounded-2xl border border-border">
                <iframe src="https://www.google.com/maps?q=10151+Enterprise+Center+Blvd+Suite+103+Boynton+Beach+FL+33437&output=embed" className="h-full w-full" style={{
                border: 0
              }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Map of Boynton Beach location"></iframe>
              </div>
              <p className="text-center font-sans text-sm text-muted-foreground">
                Boynton Beach — 10151 Enterprise Center Blvd Suite 100
              </p>
            </div>
          </div>
        </div>
      </section>
    </>;
}
